import React, { useState, useMemo, forwardRef } from 'react'
import { Box } from '@chakra-ui/react'
import { VirtuosoGrid } from 'react-virtuoso'
import type { ObjectOfBandwidthUrlAndViews } from '../../interfaces'
import AnalyticsFileElement from './AnalyticsFileElement'
import useColCountObserver from '../../hooks/useColCountObserver'

interface Props {
  data: ObjectOfBandwidthUrlAndViews[] | undefined
  showPreview: boolean
  forceimagePreview: boolean
  scrollEl: HTMLDivElement | null
}

// Create grid components similar to makeGridComponents
const makeAnalyticsGridComponents = (colCount: number) => {
  const List = forwardRef<HTMLDivElement, any>(function VirtuosoGridList(
    { children, style },
    ref,
  ) {
    const merged: React.CSSProperties = {
      ...(style as React.CSSProperties),
      display: 'grid',
      gridTemplateColumns: `repeat(${colCount}, minmax(10px, 1fr))`,
      columnGap: '16px',
      rowGap: '16px',
      width: '100%',
    }
    return (
      <div ref={ref} style={merged} className="analytics-image-grid">
        {children}
      </div>
    )
  })

  const Item = forwardRef<HTMLDivElement, any>(function VirtuosoGridItem(
    props,
    ref,
  ) {
    return <div ref={ref} {...props} />
  })

  return { List, Item }
}

const ImageGrid = ({ data, showPreview, forceimagePreview, scrollEl }: Props): React.ReactElement => {
  const filesList = data ?? []
  const [colCount, setColCount] = useState(6)
  // Use scrollEl for column count observation
  useColCountObserver(scrollEl, setColCount)

  // Memoize grid components based on colCount
  const gridComponents = useMemo(() => makeAnalyticsGridComponents(colCount), [colCount])

  if (filesList.length === 0) {
    return <Box>No data available</Box>
  }

  if (!scrollEl) {
    return <Box paddingX="24px" paddingTop="24px">Loading...</Box>
  }

  return (
    <Box 
      width="100%"

    >
      <VirtuosoGrid
        data={filesList}
        components={gridComponents}
        customScrollParent={scrollEl}
        overscan={0}
        itemContent={(index, item) => (
          <AnalyticsFileElement
            bandwidth={item.bandwidth}
            url={item.url}
            thumbnailUrl={item.thumbnailUrl}
            views={item.views}
            showPreview={showPreview}
            forceimagePreview={forceimagePreview}
          />
        )}
      />
    </Box>
  )
}

export default ImageGrid
